import DOMPurify from "dompurify";

// Allowed MIME types and size limits for document file uploads (images and PDFs)
export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/bmp",
  "application/pdf"
];

export const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".pdf"];

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB limit

/**
 * Validates an uploaded file's MIME type, extension, and file size to prevent malicious file uploads.
 */
export function validateUploadedFile(file: File): { valid: boolean; error?: string } {
  if (!file) {
    return { valid: false, error: "No file was selected." };
  }

  // 1. Validate File Size
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size (${sizeMB} MB) exceeds the maximum limit of 10 MB. Please upload a smaller image.`
    };
  }

  if (file.size === 0) {
    return { valid: false, error: "Uploaded file is empty (0 bytes)." };
  }

  // 2. Validate Extension
  const filename = file.name.toLowerCase();
  const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => filename.endsWith(ext));

  // Reject known dangerous extensions explicitly
  const DANGEROUS_EXTENSIONS = [
    ".exe", ".bat", ".cmd", ".sh", ".php", ".js", ".html", ".htm", ".vbs",
    ".jar", ".py", ".pl", ".rb", ".cgi", ".phtml", ".phar", ".dll", ".so"
  ];
  const isDangerous = DANGEROUS_EXTENSIONS.some((ext) => filename.endsWith(ext));

  if (isDangerous) {
    return {
      valid: false,
      error: `Security Alert: Executable or script file extension detected (${filename}). Only document image files (PNG, JPG, WEBP) are permitted.`
    };
  }

  // 3. Validate MIME Type
  const mimeType = file.type.toLowerCase();
  if (mimeType && !ALLOWED_IMAGE_TYPES.includes(mimeType) && !mimeType.startsWith("image/")) {
    return {
      valid: false,
      error: `Unsupported file format ("${file.type || filename}"). Only image files (PNG, JPG, WEBP, GIF, BMP) are allowed.`
    };
  }

  if (!hasValidExtension && !mimeType.startsWith("image/")) {
    return {
      valid: false,
      error: "Invalid file format. Please upload a valid image file (PNG, JPG, WEBP)."
    };
  }

  return { valid: true };
}

/**
 * Common prompt injection, jailbreak, and system override pattern signatures
 */
const PROMPT_INJECTION_PATTERNS = [
  /ignore\s+(all\s+|previous\s+|prior\s+|above\s+|system\s+)?(instructions|directions|prompts|rules|directives|commands)/gi,
  /disregard\s+(all\s+|previous\s+|prior\s+|above\s+)?(instructions|directions|prompts|guidelines)/gi,
  /forget\s+(all\s+|previous\s+|prior\s+)?(instructions|directions|prompts|context)/gi,
  /system\s*prompt\s*:/gi,
  /\[system\]/gi,
  /\[instruction\]/gi,
  /<\|im_start\|>/gi,
  /<\|im_end\|>/gi,
  /<\|startoftext\|>/gi,
  /\[INST\][\s\S]*?\[\/INST\]/gi,
  /you\s+are\s+now\s+(a|an)?\s*(unrestricted|unfiltered|jailbroken|evil|chaos|dan|godmode)/gi,
  /do\s+anything\s+now/gi,
  /\bdan\s+mode\b/gi,
  /\bjailbreak\b/gi,
  /override\s+(system|security|jailbreak|safety|guardrail)\s+(prompt|protocol|rules|directives)/gi,
  /pretend\s+(you\s+have\s+no|you\s+are\s+not\s+bound\s+by)\s+(rules|restrictions|guidelines)/gi,
  /reveal\s+(your\s+)?(system\s+prompt|initial\s+instructions|hidden\s+rules|api\s+key)/gi,
  /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
  /javascript\s*:/gi,
  /document\.cookie/gi,
  /eval\s*\(/gi,
  /exec\s*\(/gi,
  // SQL Injection pattern signatures (defense-in-depth)
  /(\b(union\s+all\s+select|union\s+select|select\s+.*\s+from|insert\s+into|drop\s+table|delete\s+from|update\s+.*\s+set)\b)|(\b(or|and)\s+['"]?1['"]?\s*=\s*['"]?1\b)|(\b(exec|execute)\s*\(|xp_cmdshell)/gi
];

export interface PromptInjectionCheckResult {
  detected: boolean;
  matchedPatterns: string[];
  sanitizedText: string;
  warning?: string;
}

/**
 * Checks extracted text or user input for prompt injection signatures or malicious code injection vectors,
 * neutralizing any dangerous system instruction overrides.
 */
export function detectAndSanitizePromptInjection(input: string): PromptInjectionCheckResult {
  if (!input) {
    return { detected: false, matchedPatterns: [], sanitizedText: "" };
  }

  let sanitized = sanitizeUserInput(input);
  const matchedPatterns: string[] = [];

  for (const basePattern of PROMPT_INJECTION_PATTERNS) {
    // Construct fresh stateless RegExp instances to guarantee zero lastIndex leakage across batches
    const testPattern = new RegExp(basePattern.source, basePattern.flags.replace("g", ""));
    if (testPattern.test(sanitized)) {
      const matchName = basePattern.source.replace(/\\s\+|\\b|\\g|i/gi, " ").trim();
      matchedPatterns.push(matchName);

      // Neutralize matched prompt injection phrase
      const replacePattern = new RegExp(basePattern.source, basePattern.flags.includes("g") ? basePattern.flags : basePattern.flags + "g");
      sanitized = sanitized.replace(replacePattern, (match) => `[FLAGGED_SYSTEM_OVERRIDE_NEUTRALIZED: "${match}"]`);
    }
  }

  const detected = matchedPatterns.length > 0;
  const warning = detected
    ? "Security Notice: Suspicious system override or prompt injection attempt detected and neutralized in input text."
    : undefined;

  return {
    detected,
    matchedPatterns,
    sanitizedText: sanitized,
    warning
  };
}

/**
 * Sanitizes user-submitted text by stripping all executable HTML tags, inline event handlers,
 * and malicious script payloads, returning clean, safe plain text.
 */
export function sanitizeUserInput(input: string): string {
  if (!input) return "";
  
  // Use DOMPurify to strip HTML tags and malicious payloads
  const clean = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [], // Disallow all HTML elements
    ALLOWED_ATTR: [], // Disallow all attributes
    KEEP_CONTENT: true // Retain text node contents
  });

  return clean;
}


